import { useEffect, useState } from 'react';

import { cn } from '@/lib/utils';

const STORAGE_KEY = 'bankiq-theme';

type Theme = 'light' | 'dark';

/**
 * Read the initial theme using a 3-tier priority:
 *
 * 1. Explicit user choice persisted in localStorage from a prior session
 * 2. System preference via `prefers-color-scheme` media query (first visit)
 * 3. Light mode as the safe default (matches Ryan's prototype default)
 *
 * The function is safe to call during render — it does not throw if
 * `window` or `matchMedia` are unavailable (e.g., during SSR or in some test
 * environments).
 */
function readInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch {
    // localStorage may be unavailable in privacy mode; fall through to system preference.
  }
  if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
  return 'light';
}

export interface ThemeToggleProps {
  className?: string;
}

/**
 * Light/dark toggle restyled to Ryan's v6 pill switch (SCRUM-321): a 42×24
 * track with an 18px thumb that slides right and turns brand-gold in dark
 * mode. Behavior is unchanged from the original icon toggle — it writes the
 * `data-theme` attribute on `<html>` and persists the choice via
 * localStorage; the palette flip happens through CSS variables in tokens.css.
 */
export function ThemeToggle({ className }: ThemeToggleProps) {
  const [theme, setTheme] = useState<Theme>(readInitialTheme);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // localStorage write failures (privacy mode, quota) are non-fatal.
    }
  }, [theme]);

  const next: Theme = theme === 'light' ? 'dark' : 'light';

  return (
    <button
      type="button"
      onClick={() => {
        setTheme(next);
      }}
      aria-label={`Switch to ${next} mode`}
      className={cn(
        'border-border-1 bg-bg-3 hover:border-border-2 rounded-tk-full relative h-6 w-[42px] border p-[2px] transition-colors',
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'rounded-tk-full block h-[18px] w-[18px] transition-all duration-200',
          theme === 'dark' ? 'bg-brand-primary translate-x-[18px]' : 'bg-[var(--text-2)]',
        )}
      />
    </button>
  );
}
